// src/lib/qrRouter.js
//
// Single source of truth for translating a raw QR string into a typed
// scan intent. The scanner component (and any future NFC reader) calls
// `routeQR(text)` and dispatches based on the returned `type`.
//
// Supported types (in priority order):
//   'join'        — ANUBIS-JOIN-{code} OR a /join/{slug} URL   invite / event join
//   'frago'       — ANUBIS-FRAGO-{fragoId}                     structured FRAGO trigger
//   'intel'       — ANUBIS-INTEL-{triggerId}                   legacy field intel
//                   (deprecated — kept routable for in-flight events; the
//                   FRAGO Rework replaces it with qr_discovery triggers)
//   'checkpoint'  — ANUBIS-CP-{checkpointId}                   checkpoint capture
//   'equipment'   — ANUBIS-EQUIP-{equipmentId}                 equipment / FRAGO objective
//   'kill'        — bare UUID (preferred) OR deathcard://claim/{uuid} (legacy)
//   'unknown'     — anything else
//
// Bare-UUID kill scans are the canonical format for player patches. Old
// patches encoded `deathcard://claim/{uuid}` and still need to work, so
// both forms route to the same handler. New player-card QRs (PlayerView,
// Profile) generate bare UUIDs only.
//
// Track 3.4 — JOIN QRs come in two flavours that both have to work:
//   1. ANUBIS-JOIN-{slug}  — in-app scan only
//   2. https://death-card.vercel.app/join/{slug}  — external camera
//      scan opens the browser straight to JoinEvent; in-app scan needs
//      this parser to extract the slug. Same handling for localhost
//      so dev / preview deploys still work.

const PREFIX = {
  JOIN:  'ANUBIS-JOIN-',
  FRAGO: 'ANUBIS-FRAGO-',
  INTEL: 'ANUBIS-INTEL-',
  CP:    'ANUBIS-CP-',
  EQUIP: 'ANUBIS-EQUIP-',
}

const KILL_URL_PREFIX = 'deathcard://claim/'

// RFC 4122 UUID with optional surrounding whitespace.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// URL-form join detection — matches the deployed origin + any localhost
// dev port + the Vercel preview origin family. Pulled out so the
// detection logic doesn't muddy routeQR's main branch list.
const JOIN_URL_HOSTS = [
  'death-card.vercel.app',
  'localhost',
  '127.0.0.1',
]
function extractJoinSlugFromUrl(text) {
  try {
    const url = new URL(text)
    const hostOk = JOIN_URL_HOSTS.some(h => url.hostname === h || url.hostname.endsWith('.vercel.app'))
    if (!hostOk) return null
    // Path looks like /join/<slug> or /join/<slug>/...
    const segments = url.pathname.split('/').filter(Boolean)
    if (segments[0] !== 'join') return null
    return segments[1] || null
  } catch {
    return null
  }
}

export function routeQR(rawText) {
  const text = (rawText || '').trim()
  if (!text) return { type: 'unknown', payload: null, raw: text }

  if (text.startsWith(PREFIX.JOIN))  return { type: 'join',       payload: text.slice(PREFIX.JOIN.length),  raw: text }
  // Track 3.4 — URL-form join links resolve to the same 'join' route as
  // the ANUBIS-JOIN- prefix. Without this an in-app scan of the event's
  // share QR (which encodes the URL for browser-camera flow) just hit
  // "Unrecognized QR code".
  if (text.startsWith('http://') || text.startsWith('https://')) {
    const slug = extractJoinSlugFromUrl(text)
    if (slug) return { type: 'join', payload: slug, raw: text }
  }
  // FRAGO prefix must be checked BEFORE intel — both share the ANUBIS-
  // prefix and FRAGO is the modern path. raw: text is preserved so the
  // handler can do the frago_trigger_qr lookup by full QR string.
  if (text.startsWith(PREFIX.FRAGO)) return { type: 'frago',      payload: text.slice(PREFIX.FRAGO.length), raw: text }
  if (text.startsWith(PREFIX.INTEL)) return { type: 'intel',      payload: text.slice(PREFIX.INTEL.length), raw: text }
  if (text.startsWith(PREFIX.CP))    return { type: 'checkpoint', payload: text.slice(PREFIX.CP.length),    raw: text }
  if (text.startsWith(PREFIX.EQUIP)) return { type: 'equipment',  payload: text.slice(PREFIX.EQUIP.length), raw: text }

  if (text.startsWith(KILL_URL_PREFIX)) {
    const id = text.slice(KILL_URL_PREFIX.length).trim()
    if (UUID_RE.test(id)) return { type: 'kill', payload: id, raw: text }
    return { type: 'unknown', payload: null, raw: text }
  }

  if (UUID_RE.test(text)) return { type: 'kill', payload: text, raw: text }

  return { type: 'unknown', payload: null, raw: text }
}

export const QR_PREFIX = PREFIX
export const KILL_QR_URL_PREFIX = KILL_URL_PREFIX
